/**
 * FCM registration lifecycle (ONE binding).
 *
 * Shared mechanics (payload builder, token slot, cadence constant) live in
 * `@aegix/web-push`; ONE's identifiers stay here: `app_name:
 * "one"`, the env-major Firebase entry name as `app_identifier`, and ONE's
 * own device-id mint. The registered token is remembered locally so logout
 * can deregister exactly what this browser registered.
 */
import {
  buildWebPushRegistrationPayload,
  createTokenStore,
} from '@aegix/web-push';
import { getOnePushConfig } from './config';
import { requestFcmToken } from './firebase';
import { registerFcmToken, deregisterFcmToken } from './fcmApi';
import { getOrCreateDeviceId, getPersistedDeviceId } from './deviceId';
// Reads workspace-root version for `app_version`, matching AIM.
// Crossing the project boundary is intentional here.
// eslint-disable-next-line @nx/enforce-module-boundaries
import packageInfo from '../../../../package.json';

/** Where the last successfully registered token is remembered. */
export const ONE_FCM_TOKEN_STORAGE_KEY = 'aegix_one_fcm_token';

/** Re-registration cadence — keeps tokens fresh and catches rotation. */
export { REREGISTER_INTERVAL_MS } from '@aegix/web-push';

const tokenStore = createTokenStore(ONE_FCM_TOKEN_STORAGE_KEY);

/**
 * Register this browser for ONE push. Called after login, on the 7-day
 * cadence, and on silent `token_refresh` pushes. Returns `true` when the
 * backend accepted a registration.
 *
 * Skips (returning `false`):
 *  - LOUDLY when the env has no Firebase Web App config (ADR-132 A4 —
 *    `getOnePushConfig` logs the console error);
 *  - quietly when the user denied notifications or the browser cannot push;
 *  - with a console.warn when the backend rejects the registration.
 */
export async function registerForPush(): Promise<boolean> {
  const config = getOnePushConfig();
  if (!config) return false;

  const token = await requestFcmToken();
  if (!token) return false;

  try {
    await registerFcmToken(
      buildWebPushRegistrationPayload({
        token,
        appName: 'one',
        appIdentifier: config.appIdentifier,
        appVersion: packageInfo.version,
        deviceId: getOrCreateDeviceId(),
      }),
    );
  } catch (err) {
    console.warn('[aegix-one push] FCM token registration failed:', err);
    return false;
  }

  // Storage-unavailable set is swallowed — registration still succeeded;
  // logout will simply have no remembered token to deregister.
  tokenStore.set(token);
  return true;
}

/**
 * Deregister this browser's FCM registration. Must run BEFORE logout tears
 * down the session: the endpoint requires an authenticated call, and ONE's
 * allauth logout performs no server-side FCM deactivation (AIM's legacy
 * `logout/` signal path is JWT-endpoint-specific).
 *
 * Never throws — a failed deregistration must not block logout.
 */
export async function deregisterForPush(): Promise<void> {
  const token = tokenStore.get();
  if (!token) return;

  try {
    await deregisterFcmToken(token, getPersistedDeviceId());
    // Forget only after the server deactivated it — a failed deregistration
    // keeps the token remembered for the next attempt.
    tokenStore.remove();
  } catch (err) {
    console.warn(
      '[aegix-one push] FCM deregistration failed (logout continues):',
      err,
    );
  }
}
